/**
 * @fileoverview
 * Hook to process cohorts from the params and
 * update params with fetched playlist.
 */

import { useEffect, useMemo } from 'react'

import { useApi } from '@core/api/hooks/useApi'
import type { Channel } from '@core/channel'
import { logWarning } from '@core/logging'
import { getBrowserLocale } from '@core/params/helpers/getBrowserLocale'
import { useStore } from '@core/widgets/store'
import { paramsSelector } from '@core/widgets/store/selectors'

interface Props {
  channel: Channel | null
}

/**
 *
 */
export function useCohorts({ channel }: Props) {
  const api = useApi()
  const actions = useStore((state) => state.actions)
  const params = useStore(paramsSelector)

  const cohortsSerialized = useMemo(
    () =>
      JSON.stringify(
        Object.entries(params)
          .filter(([key]) => key.startsWith('cohort_'))
          .sort(),
      ),
    [params],
  )

  useEffect(() => {
    ;(async () => {
      try {
        const cohortsEntries = JSON.parse(cohortsSerialized)

        if (channel && cohortsEntries.length) {
          // Process cohorts and replace country_code_locale with actual browser locale
          const processedCohorts = Object.fromEntries(
            cohortsEntries.map(([key, value]) => {
              if (
                key === 'cohort_country_code' &&
                value === 'country_code_locale'
              ) {
                return [key, getBrowserLocale() || 'en-US']
              }
              return [key, value]
            }),
          )

          const response = await api.getPlaylistFromCohorts(
            channel.username,
            processedCohorts,
          )
          actions.setCohortParams({ playlist: response.playlist_id })
        }
      } catch (err) {
        logWarning(
          `Error when fetching playlist from cohorts: ${err.data.error}`,
        )
      }
    })()
  }, [cohortsSerialized, api, actions, channel])
}
